/**
 * 교사·원장 첫 화면의 "개입 필요 학생" 목록. 판정은 서버(GET /admin/classes/:id/interventions)가 한다.
 * - 첫 오답마다 알리지 않는다. 서버가 반복 실패·장기 미수행으로 열어 둔 알림만 보인다(빨간색 + 사유 문구 + 아이콘, 색에만 의존하지 않음).
 * - 각 알림에 문항 번호, 반복 실패/지연 근거, 실시한 복습 수, 마지막 학습 시각을 함께 보인다.
 * - "확인했어요"는 교사 확인 표시일 뿐 알림을 해소하지 않는다. 해소는 학생 후속 학습 뒤 서버 재평가로만 일어난다.
 * - 전체 현황(정상·판정 불가 포함)은 "전체 현황 보기"로 따로 조회한다. 판정 불가(제출 자료 없음)는 정상과 구분해 표시한다.
 * - 임계값은 제안 기본값(원장 미확정)이며 화면에 밝힌다. 외부 알림은 보내지 않는다.
 */
import { h, clear } from './dom.js';
import { currentGen, type Transport } from '../core/transport.js';

interface Alert { id: string; condition: string; reason: string; pointNumber: string; failStreak: number; overdueDays: number; reviewsDone: number; lastStudyAt: string; teacherAcknowledged: boolean }
interface Row { studentId: string; name: string; status: 'alert' | 'ok' | 'insufficient'; reviewItems: number; alerts: Alert[] }
interface Report { counts: { total: number; attention: number; insufficient: number }; attention: Row[]; students?: Row[]; policy: { ruleVersion: string; ownerConfirmed: boolean; redFailStreak: number; redOverdueDays: number } }

const KST = { timeZone: 'Asia/Seoul', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' } as const;
const when = (iso: string): string => new Intl.DateTimeFormat('ko-KR', KST).format(new Date(iso));
const STATUS_TEXT: Record<Row['status'], string> = { alert: '개입 필요', ok: '정상', insufficient: '판정 불가(제출한 학습 기록 없음)' };

export function mountInterventions(root: HTMLElement, t: Transport): void {
  const gen = currentGen();
  const live = () => gen === currentGen() && root.isConnected;
  const body = h('div', { 'data-testid': 'iv-body' });
  clear(root);
  root.append(h('section', { class: 'ivbox', 'data-testid': 'interventions', 'aria-label': '개입 필요 학생' }, h('h2', {}, '개입 필요 학생'), body));
  let classes: { id: string; name: string }[] = [];
  let showAll = false;

  async function load(): Promise<void> {
    clear(body); body.append(h('p', { class: 'loading' }, '불러오고 있어요'));
    const c = classes.length === 0 ? await t.request('GET', '/admin/classes') : null;
    if (!live()) return;
    if (c !== null) {
      if (c.kind !== 'http' || c.status !== 200) { fail(); return; }
      classes = c.body['classes'] as typeof classes;
    }
    const reports: { cls: { id: string; name: string }; r: Report }[] = [];
    for (const cls of classes) {
      const r = await t.request('GET', `/admin/classes/${cls.id}/interventions${showAll ? '?view=all' : ''}`);
      if (!live()) return;
      if (r.kind !== 'http' || r.status !== 200) { fail(); return; } // 조회 실패는 "개입 대상 없음"이 아니다
      reports.push({ cls, r: r.body as unknown as Report });
    }
    draw(reports);
  }
  function fail(): void {
    clear(body);
    body.append(h('p', { class: 'notice', role: 'alert', 'data-testid': 'iv-error' }, '개입 목록을 불러오지 못했어요. 개입 대상이 없는 것이 아니라 받아 오지 못한 상태예요.'),
      h('button', { class: 'secondary', 'data-testid': 'iv-retry', onclick: () => void load() }, '다시 불러오기'));
  }

  function alertRow(clsId: string, s: Row, a: Alert): HTMLElement {
    const ack = h('button', { class: 'secondary', type: 'button', 'data-testid': `iv-ack-${a.id}` }, a.teacherAcknowledged ? '확인함' : '확인했어요');
    if (a.teacherAcknowledged) ack.disabled = true;
    ack.onclick = async () => {
      ack.disabled = true;
      const r = await t.request('POST', `/admin/classes/${clsId}/interventions/${a.id}/ack`, {});
      if (!live()) return;
      if (r.kind === 'http' && r.status === 200) ack.textContent = '확인함'; else { ack.disabled = false; ack.textContent = '다시 확인'; }
    };
    return h('li', { class: 'ivrow', 'data-testid': `iv-alert-${a.id}`, 'data-condition': a.condition },
      h('span', { class: 'iv-flag' }, '⚠ 개입 필요'),
      h('strong', { class: 'at' }, `${s.name} · ${a.pointNumber}번`),
      h('span', { class: 'iv-reason' }, a.reason),
      h('span', { class: 'ameta' }, `복습 ${a.reviewsDone}번 · 마지막 학습 ${when(a.lastStudyAt)}`),
      ack,
      h('span', { class: 'ev-note' }, '확인해도 알림은 학생이 후속 학습을 마칠 때까지 유지돼요.'));
  }

  function draw(reports: { cls: { id: string; name: string }; r: Report }[]): void {
    clear(body);
    const attention = reports.reduce((n, x) => n + x.r.counts.attention, 0);
    const insufficient = reports.reduce((n, x) => n + x.r.counts.insufficient, 0);
    const total = reports.reduce((n, x) => n + x.r.counts.total, 0);
    body.append(h('p', { class: 'iv-sum', 'data-testid': 'iv-summary' }, `담당 학생 ${total}명 중 개입 필요 ${attention}명 · 판정 불가 ${insufficient}명`));
    if (attention === 0) body.append(h('p', { 'data-testid': 'iv-none' }, '지금 개입이 필요한 학생이 없어요. 첫 오답만으로는 알리지 않아요.'));
    for (const { cls, r } of reports) {
      if (r.attention.length === 0) continue;
      body.append(h('div', { 'data-testid': `iv-class-${cls.id}` }, h('h3', {}, cls.name),
        h('ul', { class: 'alist' }, ...r.attention.flatMap((s) => s.alerts.map((a) => alertRow(cls.id, s, a))))));
    }
    const p = reports[0]?.r.policy;
    if (p !== undefined) body.append(h('p', { class: 'ev-note', 'data-testid': 'iv-policy' }, `기준(제안 기본값, 원장 미확정): 같은 문제를 간격 복습에서 ${p.redFailStreak}번 연속 틀리거나, 복습 예정일을 ${p.redOverdueDays}일 넘기면 알려요.`));
    const toggle = h('button', { class: 'secondary', type: 'button', 'data-testid': 'iv-all', onclick: () => { showAll = !showAll; void load(); } }, showAll ? '전체 현황 닫기' : '전체 현황 보기');
    body.append(toggle);
    if (showAll) {
      for (const { cls, r } of reports) {
        body.append(h('div', { 'data-testid': `iv-all-${cls.id}` }, h('h3', {}, `${cls.name} 전체`),
          h('ul', { class: 'alist' }, ...(r.students ?? []).map((s) => h('li', { 'data-status': s.status }, h('strong', { class: 'at' }, s.name), h('span', { class: 'ameta' }, `${STATUS_TEXT[s.status]} · 복습 항목 ${s.reviewItems}개`))))));
      }
    }
  }
  void load();
}
